// @ts-strict-ignore
import type { RegularFilterValueConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/regular-filter-value-config'
import type StatsClient from '@empathybroker/tsclient-stats'
import type { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import type { StatsParams } from '@empathybroker/tsclient-stats/dist/models/params/stats-params'
import type { StatsError } from '@empathybroker/tsclient-stats/dist/models/stats-error'
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type { AccessTokenProvider } from '@empathyco/playboard-core-domain'
import type { ActionContext } from 'vuex'

import type {
  ActionClass,
  AnalyticsState,
  DateFilter,
  DateRangeFilter,
  Filter,
  RealtimeFilter,
  RegularFilter,
} from '../../../models'
import type { ContextProvider } from '../../../providers'

import { CONTAINER } from '../../../config'

import { TYPES } from '../../../config/inversify.types'

import { FilterIdEnum, FilterTypeEnum } from '../../../enums'

import { FiltersUtils } from '../../../utils'

export class FetchStatsReportAction implements ActionClass {
  static async execute(
    this: void,
    _context: ActionContext<AnalyticsState, any>,
    report: { id: StatsReportsIdsEnum; params: StatsParams },
  ): Promise<StatsResponse | StatsError> {
    const statsClient: StatsClient = CONTAINER.get<() => StatsClient>(TYPES.StatsClient)()
    const authToken = await CONTAINER.get<AccessTokenProvider>(TYPES.AccessTokenProvider)()
    const reportParams = FetchStatsReportAction.fillReportParamsWithContextState(report.params)
    return statsClient.fetchStatsReport(authToken, report.id, reportParams)
  }

  private static fillReportParamsWithContextState(reportParams: StatsParams): StatsParams {
    let contextParams: StatsParams = {
      startDate: undefined,
      endDate: undefined,
      filters: {},
    }
    const { filters } = CONTAINER.get<ContextProvider>(TYPES.ContextProvider)()
    const cleanedFilters = FiltersUtils.clearRegularFiltersSelectedAll(
      FiltersUtils.filterBySection(filters, 'insights'),
    )
    Object.values(cleanedFilters).forEach((filter: Filter) => {
      if (filter.config.enabled && filter.config.id !== FilterIdEnum.COMPARE) {
        contextParams = {
          [FilterTypeEnum.DATE]: () =>
            FetchStatsReportAction.fillReportParamsWithDateFilter(
              contextParams,
              filter as DateFilter,
            ),
          [FilterTypeEnum.DATE_RANGE]: () =>
            FetchStatsReportAction.fillReportParamsWithDateRangeFilter(
              contextParams,
              filter as DateRangeFilter,
            ),
          [FilterTypeEnum.REALTIME]: () =>
            FetchStatsReportAction.fillReportParamsWithRealtimeFilter(
              contextParams,
              filter as RealtimeFilter,
            ),
          [FilterTypeEnum.REGULAR]: () =>
            FetchStatsReportAction.fillReportParamsWithRegularFilter(
              contextParams,
              filter as RegularFilter,
            ),
        }[filter.config.type]()
      }
    })

    return {
      ...contextParams,
      ...reportParams,
      filters: {
        ...contextParams.filters,
        ...reportParams.filters,
      },
    }
  }

  private static fillReportParamsWithDateFilter(
    reportParams: StatsParams,
    filter: DateFilter,
  ): StatsParams {
    const startDateInMillis = filter.selectedValues[0]
    const endDateInMillis = filter.selectedValues[1]
    if (!!startDateInMillis && !!endDateInMillis) {
      reportParams.startDate = new Date(startDateInMillis)
      reportParams.endDate = new Date(endDateInMillis)
    }
    return reportParams
  }

  private static fillReportParamsWithDateRangeFilter(
    reportParams: StatsParams,
    filter: DateRangeFilter,
  ): StatsParams {
    const startDate = filter.selectedValues[0]
    const endDate = filter.selectedValues[1]
    if (!!startDate && !!endDate) {
      reportParams.startDate = new Date(startDate)
      reportParams.endDate = new Date(endDate)
    }
    return reportParams
  }

  private static fillReportParamsWithRealtimeFilter(
    reportParams: StatsParams,
    filter: RealtimeFilter,
  ): StatsParams {
    if (filter.selectedValues) {
      reportParams.endDate = new Date()
      reportParams.startDate = new Date(
        reportParams.endDate.getTime() - filter.selectedValues * 60 * 1000,
      )
    }
    return reportParams
  }

  private static fillReportParamsWithRegularFilter(
    reportParams: StatsParams,
    filter: RegularFilter,
  ): StatsParams {
    reportParams.filters[filter.config.taggingId || filter.config.id] = filter.selectedValues.map(
      (selectedValue: string) => {
        const selectedValueConfig = filter.config.values.find(
          (filterValueConfig: RegularFilterValueConfig) => filterValueConfig.id === selectedValue,
        )
        return selectedValueConfig.taggingId || selectedValueConfig.id
      },
    )
    return reportParams
  }
}
